import React, { useState, useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Box, Container, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { AnimatePresence, motion } from "framer-motion";
import { useSelector } from "react-redux";
import { Search, ArrowRight } from "lucide-react";

const HeroSection = () => {
  const [currentSector, setCurrentSector] = useState(0);
  const i18nLocale = useSelector((state) => state.i18nLocale);
  const locale = i18nLocale || "fr";
  const intl = useIntl();
  const theme = useTheme();
  const sectors = [
    intl.formatMessage({ id: "hero_section.sector_architecture" }),
    intl.formatMessage({ id: "hero_section.sector_engineering" }),
    intl.formatMessage({ id: "hero_section.sector_construction" }),
    intl.formatMessage({ id: "hero_section.sector_administration" }),
    intl.formatMessage({ id: "hero_section.sector_industry" }),
  ];

  const filterPills = [
    { label: intl.formatMessage({ id: "hero_section.architecture"}), icon: "🏛️", href: `/${locale}/job-offers/architecture` },
    { label: intl.formatMessage({ id: "hero_section.engineering"}), icon: "⚙️", href: `/${locale}/job-offers/engineering` },
    { label: intl.formatMessage({ id: "hero_section.construction"}), icon: "🏗️", href: `/${locale}/job-offers/construction` },
    { label: intl.formatMessage({ id: "hero_section.administration"}), icon: "💼", href: `/${locale}/job-offers/administration` },
    { label: intl.formatMessage({ id: "hero_section.mason"}), icon: "🧱", href: `/${locale}/job-offers/construction/mason` },
    { label: intl.formatMessage({ id: "hero_section.bim"}), icon: "📐", href: `/${locale}/job-offers/construction/BIM` },
    { label: intl.formatMessage({ id: "hero_section.hvac"}), icon: "❄️", href: `/${locale}/job-offers/engineering/hvac` },
    { label: intl.formatMessage({ id: "hero_section.estimator"}), icon: "🧮", href: `/${locale}/job-offers/construction/estimator` },
    { label: intl.formatMessage({ id: "hero_section.electrician"}), icon: "⚡", href: `/${locale}/job-offers/engineering/electrical-engineer` },
    { label: intl.formatMessage({ id: "hero_section.environment"}), icon: "🌿", href: `/${locale}/job-offers/construction/sustainable-construction` },
    { label: intl.formatMessage({ id: "hero_section.urbanism"}), icon: "🗺️", href: `/${locale}/job-offers/architecture/urbanism` },
    { label: intl.formatMessage({ id: "hero_section.heavy_truck_driver"}), icon: "🚚", href: `/${locale}/job-offers/construction/heavy-truck-driver` },
    { label: intl.formatMessage({ id: "hero_section.joiner"}), icon: "🪚", href: `/${locale}/job-offers/construction/joiner` },
    { label: intl.formatMessage({ id: "hero_section.heating_engineer"}), icon: "🔥", href: `/${locale}/job-offers/construction/heating-engineer` },
    { label: intl.formatMessage({ id: "hero_section.team_leader"}), icon: "👷", href: `/${locale}/job-offers/construction/team-leader` },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSector((prev) => (prev + 1) % sectors.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Box
      component="section"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: { xs: "70vh", md: "92vh" },
        py: { xs: "2rem", md: 0 },
        backgroundColor: "hsl(178, 93%, 10%)",
      }}
    >
      <Container maxWidth="lg">
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          sx={{ mb: "2rem" }}
        >
          <Box
            component="span"
            sx={{
              display: { xs: "none", md: "inline-flex" },
              alignItems: "center",
              gap: "0.5rem",
              px: "1rem",
              py: "0.5rem",
              borderRadius: "9999px",
              bgcolor: "hsl(153 68% 25% / 0.15)",
              border: "1px solid hsl(153 68% 25% / 0.2)",
              color: "hsl(153 68% 40%)",
              fontSize: "0.875rem",
              fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
              backdropFilter: "blur(4px)",
            }}
          >
            <Box
              component="span"
              sx={{
                width: "0.5rem",
                height: "0.5rem",
                borderRadius: "9999px",
                bgcolor: "hsl(153 68% 25%)",
                animation: "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
                "@keyframes pulse": {
                  "0%, 100%": { opacity: 1 },
                  "50%": { opacity: 0.5 },
                },
              }}
            />
            <FormattedMessage id="hero_section.ai_badge" />
          </Box>
        </Box>

        <Box
          component={motion.h1}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          sx={{
            fontSize: {
              xs: "2.25rem",
              sm: "3rem",
              md: "3.75rem",
              lg: "4.5rem",
            },
            fontFamily: "Segoe UI Black, system-ui, sans-serif",
            color: theme.palette.common.white,
            lineHeight: 1.05,
            mb: "20px",
            letterSpacing: "-0.025em",
          }}
        >
          <FormattedMessage id="hero_section.title_line_1" />
          <br />
          <Box
            component="span"
            sx={{
              display: "inline-block",
              verticalAlign: "bottom",
              height: "calc(1.2em + 4px)",
              lineHeight: "calc(1.2em + 4px)",
            }}
          >
            <FormattedMessage id="hero_section.title_line_2" />
          </Box>{" "}
          <Box
            component="span"
            sx={{
              position: "relative",
              display: "inline-block",
              overflow: "hidden",
              verticalAlign: "bottom",
              height: "calc(1.2em + 4px)",
              minWidth: "14ch",
            }}
          >
            <AnimatePresence mode="wait">
              <Box
                component={motion.span}
                key={currentSector}
                initial={{ opacity: 0, y: 50, rotateX: -40 }}
                animate={{ opacity: 1, y: 0, rotateX: 0 }}
                exit={{ opacity: 0, y: -50, rotateX: 40 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                sx={{
                  display: "inline-block",
                  fontFamily: "Segoe UI Black, system-ui, sans-serif",
                  px: "1rem",
                  py: "0.25rem",
                  background:
                  "linear-gradient(to right, hsl(153 68% 25%), hsl(153 68% 25% / 0.8))",
                  borderRadius: "0.75rem",
                  color: theme.palette.common.white,
                  position: "absolute",
                  top: 0,
                  left: 0,
                }}
              >
                {sectors[currentSector]}
              </Box>
            </AnimatePresence>
          </Box>
        </Box>
        <Box
          component={motion.p}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          sx={{
            color: alpha(theme.palette.common.white, 0.5),
            fontSize: "20px",
            mb: "2.5rem",
            maxWidth: "32rem",
            lineHeight: "28px",
            fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
          }}
        >
          <FormattedMessage id="hero_section.description_line_1" />
          <Box component="br" sx={{ display: { xs: "none", sm: "block" } }} />
          <FormattedMessage id="hero_section.description_line_2" />
        </Box>

        <Box
          component={motion.form}
          action={`/${locale}/job-offers`}
          method="get"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          sx={{
            maxWidth: "42rem",
            width: "100%",
            display: "flex",
            alignItems: "center",
            bgcolor: alpha(theme.palette.background.paper, 0.95),
            borderRadius: "1rem",
            overflow: "hidden",
            border: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
            transition: "all 0.3s ease",
            "&:focus-within": {
              borderColor: "rgba(20, 107, 66, 0.5)",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flex: 1,
              px: "1.25rem",
              py: "1rem",
              gap: "0.75rem",
            }}
          >
            <Search size={20} color="rgb(103, 126, 125)" />
            <Box
              component="input"
              type="text"
              name="q"
              placeholder={intl.formatMessage({ id: "hero_section.search_placeholder" })}
              sx={{
                width: "100%",
                bgcolor: "transparent",
                border: "none",
                outline: "none",
                color: "rgb(2, 49, 48)",
                fontSize: "1rem",
                fontWeight: 500,
                lineHeight: 1.2,
                fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                transform: "translateY(-1px)",
                "&::placeholder": {
                  color: "rgba(107, 114, 128, 1)",
                  opacity: 1,
                },
              }}
            />
          </Box>
          <Box
            component="button"
            type="submit"
            sx={{
              px: "2rem",
              py: "1rem",
              m: "0.375rem",
              border: "none",
              borderRadius: "0.75rem",
              cursor: "pointer",
              bgcolor: "hsl(153 68% 25%)",
              color: theme.palette.common.white,
              fontWeight: 700,
              fontSize: "0.875rem",
              whiteSpace: "nowrap",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              transition: "all 0.3s ease",
              fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
              "&:hover": {
                bgcolor: "hsl(153 68% 25% / 0.9)",
                ".arrow-right": {
                  transform: "translateX(3px)",
                },
              },
              "& .arrow-right": {
                transition: "transform 0.3s ease",
              },
            }}
          >
            {intl.formatMessage({ id: "hero_section.search_button" })} <Box component="span">
              <ArrowRight
                size={14}
                color="white"
                style={{ display: "block" }}
                className="arrow-right" /></Box>
          </Box>
        </Box>

        <Box
          component={motion.div}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          sx={{
            mt: "2rem",
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: "0.5rem",
          }}
        >
          {filterPills.map((pill) => (
            <Box
              key={pill.label}
              component="a"
              href={pill.href}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "0.375rem",
                px: "1rem",
                py: "0.5rem",
                borderRadius: "0.75rem",
                fontSize: "0.75rem",
                fontWeight: 700,
                textDecoration: "none",
                bgcolor: alpha(theme.palette.common.white, 0.06),
                color: alpha(theme.palette.common.white, 0.7),
                border: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
                transition: "all 0.3s ease",
                backdropFilter: "blur(8px)",
                fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                "&:hover": {
                  bgcolor: "rgba(20, 107, 66, 0.2)",
                  color: "hsl(153 68% 60%)",
                  borderColor: "rgba(20, 107, 66, 0.3)",
                },
              }}
            >
              <Box component="span" sx={{ fontSize: "0.9rem" }}>{pill.icon}</Box>
              {pill.label}
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default HeroSection;
